import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { theme, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { Field, ModalHeader, ModalOverlay, input, textarea, secondaryBtn } from "./shared";

// "Report an incident" — open to every member (ISO 27001 A.6.8). Reporting is not managing:
// POST /resilience/incidents/report takes what the reporter saw and opens the incident at
// medium severity for whoever holds resilience:write to triage. The notification clocks start
// at once, on purpose (routes/resilience.routes.ts).

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "phishing", label: "Suspicious email or message" },
  { id: "lost_or_stolen_device", label: "Lost or stolen device" },
  { id: "unauthorised_access", label: "Someone got in who should not have" },
  { id: "data_breach", label: "Personal or confidential data exposed" },
  { id: "malware", label: "Virus or malware" },
  { id: "social_engineering", label: "Someone tried to trick me" },
  { id: "physical", label: "Physical security (doors, visitors, premises)" },
  { id: "system_failure", label: "A system stopped working" },
  { id: "other", label: "Something else" },
];

export function ReportIncidentButton({ onReported }: { onReported?: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [personalData, setPersonalData] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reset = () => { setTitle(""); setDescription(""); setCategory("other"); setPersonalData(false); setError(null); setDone(null); };
  const close = () => { setOpen(false); reset(); };

  async function submit() {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ incident: { displayId: number | null } }>("/resilience/incidents/report", {
        title: title.trim(), description: description.trim(), category, personalDataInvolved: personalData,
      });
      setDone(res.incident.displayId !== null ? `INC-${String(res.incident.displayId).padStart(3, "0")}` : "your report");
      onReported?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not send the report"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setOpen(true)}>
        <AlertTriangle size={14} /> Report an incident
      </button>
      {open && (
        <ModalOverlay onClose={close}>
          <ModalHeader title="Report an incident" onClose={close} />
          <div style={{ padding: 20, display: "grid", gap: 12 }}>
            {done ? (
              <>
                <div style={{ ...text.body }}>
                  Thank you — {done} has been recorded and the people who handle incidents have been told.
                  If anything changes, or you remember more, tell them directly.
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" style={btnPrimary} onClick={close}>Close</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ ...text.hint }}>
                  Report anything that looks wrong, even if you are not sure it is an incident. Do not try to investigate it yourself.
                </div>
                <Field label="What happened, in a line">
                  <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} style={input} placeholder="e.g. Email asking me to re-enter my password" autoFocus />
                </Field>
                <Field label="What kind of thing is it?">
                  <select value={category} onChange={(e) => setCategory(e.target.value)} style={input}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Details (optional)" help="When you noticed it, what you saw, anything you clicked or opened.">
                  <textarea value={description} maxLength={10_000} onChange={(e) => setDescription(e.target.value)} style={{ ...textarea, minHeight: 90 }} />
                </Field>
                <label style={{ display: "flex", gap: 8, alignItems: "center", ...text.body }}>
                  <input type="checkbox" checked={personalData} onChange={(e) => setPersonalData(e.target.checked)} />
                  It may involve personal data (customers', staff's or anyone else's)
                </label>
                {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" style={secondaryBtn} onClick={close}>Cancel</button>
                  <button type="button" style={{ ...btnPrimary, opacity: busy || !title.trim() ? 0.6 : 1 }} disabled={busy || !title.trim()} onClick={() => void submit()}>
                    {busy ? "Sending…" : "Send report"}
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
